import { motion } from "framer-motion";
import { ArrowUpRight, Wrench, Cog, ShieldCheck, Accessibility } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const services = [
  {
    id: "installation",
    testid: SERVICES.cardInstallation,
    overline: "01 · New Construction",
    title: "Elevator Installation",
    desc: "Traction, MRL and hydraulic elevator systems for high-rise, mid-rise and residential builds across the GTA. Turnkey from shaft design to final inspection.",
    icon: Cog,
    tags: ["Residential", "Commercial", "MRL / Traction"],
    image: null,
  },
  {
    id: "modernization",
    testid: SERVICES.cardModernization,
    overline: "02 · Retrofit & Upgrade",
    title: "Modernization",
    desc: "Bring aging elevators into 2026 — new controllers, VVVF drives, LED cabs, destination dispatch and full code compliance. Extend service life by decades.",
    icon: Wrench,
    tags: ["Controllers", "Drives", "Cab Interiors"],
    image:
      "https://images.unsplash.com/photo-1600167990118-5ca20ea216fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBlbGV2YXRvciUyMGludGVyaW9yfGVufDB8fHx8MTc4MzEyNTU0OXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "maintenance",
    testid: SERVICES.cardMaintenance,
    overline: "03 · 24/7 Coverage",
    title: "Maintenance & Repair",
    desc: "Preventative service programs and round-the-clock emergency repair. Our mechanics are dispatched from three depots across the GTA — 60 minute typical response.",
    icon: ShieldCheck,
    tags: ["Preventative", "Emergency", "Compliance"],
    image:
      "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxlbGV2YXRvciUyMG1haW50ZW5hbmNlJTIwZW5naW5lZXJ8ZW58MHx8fHwxNzgzMTI1NTQ5fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "accessibility",
    testid: SERVICES.cardAccessibility,
    overline: "04 · AODA Ready",
    title: "Accessibility Lifts",
    desc: "LULA, wheelchair platform lifts and residential home elevators. AODA and Ontario Building Code compliant — designed for hospitals, schools and homes.",
    icon: Accessibility,
    tags: ["LULA", "Platform Lifts", "Home Elevators"],
    image: null,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid={SERVICES.root}
      className="border-b border-[#E5E5E5] py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#FF5E00] mb-4">
              What We Build
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
              Four disciplines. One
              <br /> vertical standard.
            </h2>
          </div>
          <p className="max-w-md text-[#525252] text-base leading-relaxed">
            Vertex is a full-service elevator contractor. Whether you&rsquo;re breaking
            ground or bringing a 1970s freight lift into modern compliance —
            we&rsquo;ve done it, hundreds of times, across Toronto.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#E5E5E5]">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                data-testid={s.testid}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 2) * 0.1, duration: 0.55 }}
                className={`group relative p-8 md:p-10 bg-white hover:bg-[#F9F9F9] transition-colors border-b md:border-b-0 last:border-b-0 ${
                  i % 2 === 0 ? "md:border-r" : ""
                } ${i < 2 ? "md:border-b" : ""} border-[#E5E5E5]`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 border border-[#0A0A0A] flex items-center justify-center bg-white group-hover:bg-[#FF5E00] group-hover:border-[#FF5E00] group-hover:text-white transition-colors">
                        <Icon size={18} strokeWidth={2} />
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#525252]">
                        {s.overline}
                      </div>
                    </div>

                    <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight">
                      {s.title}
                    </h3>

                    <p className="mt-4 text-[#525252] text-base leading-relaxed max-w-md">
                      {s.desc}
                    </p>

                    <div className="mt-6 flex flex-wrap gap-2">
                      {s.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] font-bold uppercase tracking-[0.24em] border border-[#0A0A0A] px-3 py-1.5"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  {s.image && (
                    <div className="hidden lg:block w-40 h-56 shrink-0 overflow-hidden border border-[#0A0A0A]">
                      <img
                        src={s.image}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#525252]">
                    Serving Toronto & GTA
                  </span>
                  <ArrowUpRight
                    size={22}
                    className="text-[#0A0A0A] group-hover:text-[#FF5E00] transition-all group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
